<template>
  <div class="measurement-overview">
    <div
      v-for="(measurement, i) in measurementsList"
      :key="i"
      :class="[
        'measurement-overview__item',
        {
          'measurement-overview__item--favorite': i === measurements.length - 2,
        },
      ]"
    >
      <h2 v-if="i === 0">{{ t('measurement.start') }}</h2>
      <h2 v-if="measurements.length > 1 && i === measurements.length - 2">
        {{ t('measurement.final') }}
      </h2>
      <h2
        v-if="
          i <= measurements.length - 2 &&
          i !== 0 &&
          i !== measurements.length - 2
        "
      >
        {{ `#${i}` }}
      </h2>

      <div class="measurement-overview__section">
        <h3>{{ t('common.bike.saddle') }}</h3>
        <div class="measurement-overview__row">
          <strong>
            <span v-if="measurement.saddle.active"
              >{{ measurement.saddle.active.SADDLE_BRAND }}
              {{ measurement.saddle.active.SADDLE_MODEL }}</span
            >
            <span v-else>-</span>
          </strong>
        </div>
        <div class="measurement-overview__row">
          <label>{{ t('common.position.x') }}</label>
          <span
            >{{ `${measurement.saddle.x} ${t('common.unit.mm')}` }}
            <span class="measurement-overview__difference"
              >({{
                `${measurement.saddle.x - firstMeasurement.saddle.x} ${t('common.unit.mm')}`
              }})</span
            ></span
          >
        </div>
        <div class="measurement-overview__row">
          <label>{{ t('common.position.y') }}</label>
          <span
            >{{ `${measurement.saddle.y} ${t('common.unit.mm')}` }}
            <span class="measurement-overview__difference"
              >({{
                `${measurement.saddle.y - firstMeasurement.saddle.y} ${t('common.unit.mm')}`
              }})</span
            ></span
          >
        </div>
      </div>

      <div class="measurement-overview__section">
        <h3>{{ t('common.bike.handlebar') }}</h3>
        <div class="measurement-overview__row">
          <strong>
            <span v-if="measurement.handlebar.active"
              >{{ measurement.handlebar.active.HANDLEBAR_BRAND }}
              {{ measurement.handlebar.active.HANDLEBAR_MODEL }}</span
            >
            <span v-else>-</span>
          </strong>
        </div>
        <div class="measurement-overview__row">
          <label>{{ t('common.position.x') }}</label>
          <span
            >{{ `${measurement.handlebar.x * -1} ${t('common.unit.mm')}` }}
            <span class="measurement-overview__difference"
              >({{
                `${(measurement.handlebar.x - firstMeasurement.handlebar.x) * -1} ${t('common.unit.mm')}`
              }})</span
            ></span
          >
        </div>
        <div class="measurement-overview__row">
          <label>{{ t('common.position.y') }}</label>
          <span
            >{{ `${measurement.handlebar.y} ${t('common.unit.mm')}` }}
            <span class="measurement-overview__difference"
              >({{
                `${measurement.handlebar.y - firstMeasurement.handlebar.y} ${t('common.unit.mm')}`
              }})</span
            ></span
          >
        </div>
      </div>

      <div class="measurement-overview__section">
        <h3>{{ t('common.bike.crankset') }}</h3>
        <div class="measurement-overview__row">
          <span
            v-if="measurement.crankset.active"
            class="measurement-overview__size-x"
            >{{ measurement.crankset.active }} {{ t('common.unit.mm') }}</span
          >
          <span v-else>-</span>
        </div>
      </div>

      <div class="measurement-overview__section">
        <h3>{{ t('common.bike.pedals') }}</h3>
        <div class="measurement-overview__row">
          <strong>
            <span v-if="measurement.pedal.active"
              >{{ measurement.pedal.active.PEDAL_BRAND }}
              {{ measurement.pedal.active.PEDAL_MODEL }}</span
            >
            <span v-else>-</span>
          </strong>
          <div
            v-if="measurement.pedal.active?.PEDAL_HEIGHT"
            class="measurement-overview__size-y"
          >
            {{ measurement.pedal.active?.PEDAL_HEIGHT }}
            {{ t('common.unit.mm') }}
          </div>
        </div>
      </div>

      <BfButton
        :state="i === 0 ? ButtonState.Disabled : ButtonState.Enabled"
        @click="deleteMeasurement(i)"
        >{{ t('common.delete') }}</BfButton
      >
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useMeasurementStore } from '@/store/measurementStore'
import { storeToRefs } from 'pinia'
import { ButtonState, BfButton } from '@finetic/bikefitting-ui'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const measurementStore = useMeasurementStore()
const { measurements, firstMeasurement } = storeToRefs(measurementStore)

const measurementsList = computed(() => {
  if (measurements.value.length <= 1) {
    return measurements.value
  }

  return measurements.value.slice(0, -1)
})

const deleteMeasurement = (index: number) => {
  measurementStore.delete(index)
}
</script>

<style lang="scss">
.measurement-overview {
  display: flex;
  gap: 2.4rem;
  padding: 0 2.4rem;

  &__item {
    background-color: var(--color-dark-5);
    width: 24rem;
    padding: 2.4rem;
    border-radius: 1.2rem;
    border: solid 2px transparent;

    &--favorite {
      border: solid 2px var(--color-primary);
    }

    h2 {
      span {
        color: var(--color-dark-30);
      }
    }
  }

  &__section {
    margin-bottom: 2.4rem;

    h3 {
      color: var(--color-dark-40);
      margin-bottom: 0.4rem;
    }
  }

  &__row {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;

    label {
      display: inline-block;
      width: 2.4rem;
      font-weight: 700;
    }
  }

  &__difference {
    color: var(--color-dark-40);
  }

  &__size-x,
  &__size-y {
    background-size: 16px 16px;
    background-repeat: no-repeat;
    background-position: center left;
    background-image: url(@/assets/size-x.svg);
    padding-left: 24px;
  }

  &__size-y {
    background-image: url(@/assets/size-y.svg);
  }
}
</style>
